import { type OrderCardItem } from '../../OrderCardList/OrderCardList';
import { type OrderInfoCardData } from '../../OrderInfoCard/OrderInfoCard';
import { type OrderTrackingCardData } from '../../OrderTrackingCard/OrderTrackingCard';
import { type OrderCardBlock } from '../../order-card/order-card-types';

export function parseOrderList(text: string): {
  orderList: OrderCardItem[] | null;
  cleanedText: string;
} {
  const orderListRegex = /\[ORDER_LIST\]([\s\S]*?)\[\/ORDER_LIST\]/;
  const match = text.match(orderListRegex);
  if (match) {
    let orderList: OrderCardItem[] | null = null;
    try {
      const parsed = JSON.parse(match[1].trim()) as { orders?: OrderCardItem[] };
      if (Array.isArray(parsed?.orders)) {
        orderList = parsed.orders;
      }
    } catch {}
    let cleanedText = text.replace(orderListRegex, '');
    cleanedText = cleanedText.replace(/\[ORDER_LIST[-_A-Z]*$/i, '');
    return { orderList, cleanedText: cleanedText.trim() };
  }

  const cleanedText = text.replace(/\[ORDER_LIST[-_A-Z]*$/i, '');
  return { orderList: null, cleanedText };
}

export function parseOrderInfo(text: string): {
  orderInfo: OrderInfoCardData | null;
  cleanedText: string;
} {
  const orderInfoRegex = /\[ORDER_INFO\]([\s\S]*?)\[\/ORDER_INFO\]/;
  const match = text.match(orderInfoRegex);
  if (match) {
    let orderInfo: OrderInfoCardData | null = null;
    try {
      const parsed = JSON.parse(match[1].trim()) as OrderInfoCardData;
      if (parsed && typeof parsed.orderNumber === 'string') {
        // Casten är en lögn kompilatorn tror på — blocket kommer som JSON och
        // kan ha vad som helst i objektfälten. Kortet läser dem som objekt.
        orderInfo = {
          ...parsed,
          timeline:
            parsed.timeline && typeof parsed.timeline === 'object' ? parsed.timeline : undefined,
          delivery:
            parsed.delivery && typeof parsed.delivery === 'object' ? parsed.delivery : undefined,
        };
      }
    } catch {}
    let cleanedText = text.replace(orderInfoRegex, '');
    cleanedText = cleanedText.replace(/\[ORDER_INFO[-_A-Z]*$/i, '');
    return { orderInfo, cleanedText: cleanedText.trim() };
  }

  const cleanedText = text.replace(/\[ORDER_INFO[-_A-Z]*$/i, '');
  return { orderInfo: null, cleanedText };
}

export function parseOrderTracking(text: string): {
  orderTracking: OrderTrackingCardData | null;
  cleanedText: string;
} {
  const trackingRegex = /\[ORDER_TRACKING\]([\s\S]*?)\[\/ORDER_TRACKING\]/;
  const match = text.match(trackingRegex);
  if (match) {
    let orderTracking: OrderTrackingCardData | null = null;
    try {
      const parsed = JSON.parse(match[1].trim()) as OrderTrackingCardData;
      if (parsed && typeof parsed.orderNumber === 'string' && parsed.orderNumber.length > 0) {
        orderTracking = parsed;
      }
    } catch {}
    let cleanedText = text.replace(trackingRegex, '');
    cleanedText = cleanedText.replace(/\[ORDER_TRACKING[-_A-Z]*$/i, '');
    return { orderTracking, cleanedText: cleanedText.trim() };
  }

  const cleanedText = text.replace(/\[ORDER_TRACKING[-_A-Z]*$/i, '');
  return { orderTracking: null, cleanedText };
}

// Ett halvt block mitt i strömmen gömms helt: annars syns rå JSON i bubblan
// tills avslutningstaggen har kommit.
const PARTIAL_ORDER_CARD = /\[ORDER_CARD\][\s\S]*$|\[ORDER_C?A?R?D?[-_A-Z]*$/;

export function parseOrderCard(text: string): {
  orderCard: OrderCardBlock | null;
  cleanedText: string;
} {
  const cardRegex = /\[ORDER_CARD\]([\s\S]*?)\[\/ORDER_CARD\]/;
  const match = text.match(cardRegex);
  if (match) {
    let orderCard: OrderCardBlock | null = null;
    try {
      const parsed = JSON.parse(match[1].trim()) as Partial<OrderCardBlock>;
      const orders = Array.isArray(parsed?.orders)
        ? parsed.orders.filter((order) => typeof order?.orderNumber === 'string' && order.orderNumber)
        : [];
      if (typeof parsed?.view === 'string' && orders.length > 0) {
        orderCard = { view: parsed.view, orders };
      }
    } catch {}
    return { orderCard, cleanedText: text.replace(cardRegex, '').trim() };
  }

  if (PARTIAL_ORDER_CARD.test(text)) {
    return { orderCard: null, cleanedText: text.replace(PARTIAL_ORDER_CARD, '').trim() };
  }
  return { orderCard: null, cleanedText: text };
}

export function stripAsciiSeparators(text: string): string {
  return text
    .replace(
      /^\s*[ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã‚ÂÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã‚ÂÃƒâ€šÃ‚ÂÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â¢Ãƒâ€šÃ‚Â\-]{8,}\s*$/gmu,
      '',
    )
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export function stripDuplicateOrderSections(text: string): string {
  const sectionBlockRegex =
    /(?:^|\n)(?:#{1,6}\s*)?(Produkter i din order|Produkter i din senaste order(?:\s*\([^\n]+\))?|Dina senaste ordrar|Tidigare ordrar)\s*\n(?:[\s>*\-ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€šÃ‚Â¢\d\.).:,_`A-Za-zÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â¦ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¾ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã¢â‚¬Å“ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¥ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¤ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¶].*\n)*/gimu;

  return text
    .replace(sectionBlockRegex, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export function isOrderFocusedAssistantMessage(text: string): boolean {
  const normalized = text.toLowerCase();

  const returnDominantSignals = [
    /returnera\s+(din|en|denna)\s+order/i,
    /starta\s+returen/i,
    /f(?:ö|o)r\s+att\s+returnera/i,
    /returportal/i,
    /retur(?:handling|etikett)/i,
    /\bretur(?:process|en)\b/i,
    /\bå(?:nger|ngerr)(?:ätt|att)\b/i,
    /returnera\s+varan/i,
    /starta\s+en\s+retur/i,
  ];
  if (returnDominantSignals.some((pattern) => pattern.test(normalized))) {
    return false;
  }

  const orderSignals = [
    /\border\b/i,
    /ordernummer/i,
    /orderstatus/i,
    /leverans/i,
    /spÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¥r/i,
    /produkter i din (senaste )?order/i,
    /dina senaste ordrar/i,
  ];

  const outfitSignals = [
    /outfit/i,
    /look/i,
    /stil/i,
    /klÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¤d|plagg/i,
    /skjorta|kavaj|byxa|trÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¶ja|jeans/i,
    /alternativ fÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¶r/i,
  ];

  const orderScore = orderSignals.reduce(
    (score, pattern) => score + (pattern.test(normalized) ? 1 : 0),
    0,
  );
  const outfitScore = outfitSignals.reduce(
    (score, pattern) => score + (pattern.test(normalized) ? 1 : 0),
    0,
  );

  return orderScore > 0 && orderScore > outfitScore;
}
